import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, AlertTriangle, CheckCircle2, Droplets, Thermometer, ChevronDown } from 'lucide-react';
import type { AssessmentResult } from '../types/calculator';

interface SalinitySectionProps {
  result: AssessmentResult;
}

export default function SalinitySection({ result }: SalinitySectionProps) {
  const s = result.salinity;

  // Guard: if salinity data is missing, show a message
  if (!s) {
    return (
      <div className="rb-card p-8 text-center">
        <Zap className="w-8 h-8 text-slate-600 mx-auto mb-3" />
        <p className="text-sm text-slate-400">Run an assessment to generate salinity analysis.</p>
      </div>
    );
  }

  const isSaline = (s.probabilityPercent ?? 0) >= 50;

  const safeNum = (n: number | undefined | null) => (typeof n === 'number' ? n : 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-rb-brand/10 border border-rb-brand/20 flex items-center justify-center">
          <Zap className="w-5 h-5 text-rb-brand" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-white">Salinity Assessment</h2>
          <p className="text-xs text-slate-500">Probability analysis of saline water based on conductivity and TDS</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Salinity Probability */}
        <div className={`p-4 rounded-xl border-2 ${isSaline ? 'border-red-500/20 bg-red-500/5' : 'border-emerald-500/20 bg-emerald-500/5'}`}>
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Salinity Probability</h3>
          <div className="text-3xl font-bold mb-1 text-white">{(safeNum(s.probabilityPercent)).toFixed(1)}%</div>
          <div className="text-xs text-slate-400 mb-4">{s.probabilityLabel || '—'} — {s.confidenceLevel || '—'}</div>
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden border border-slate-700">
            <motion.div className="h-full bg-gradient-to-r from-cyan-400 to-cyan-500 rounded-full" initial={{ width: 0 }} animate={{ width: `${Math.min(100, safeNum(s.probabilityPercent))}%` }} transition={{ duration: 0.7 }} />
          </div>
        </div>

        {/* Classification */}
        <div className={`p-4 rounded-xl border-2 ${isSaline ? 'border-red-500/20 bg-red-500/5' : 'border-emerald-500/20 bg-emerald-500/5'}`}>
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Water Classification</h3>
          <div className="text-xl font-bold mb-1 text-white">{s.classification || '—'}</div>
          <div className="text-xs text-slate-400 mb-4">
            {isSaline ? 'Based on high conductivity/TDS levels' : 'Within freshwater range'}
          </div>
          <div className="text-[10px] text-slate-500">
            Conductivity: {safeNum(s.conductivity).toLocaleString()} µS/cm | TDS: {safeNum(s.tds).toLocaleString()} mg/L
          </div>
        </div>

        {/* Warnings */}
        <div className={`p-4 rounded-xl border-2 ${isSaline ? 'border-red-500/20 bg-red-500/5' : 'border-emerald-500/20 bg-emerald-500/5'}`}>
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">
            {isSaline ? (
              <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Warnings</span>
            ) : (
              <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Status</span>
            )}
          </h3>
          <div className="space-y-2">
            {(s.warnings && s.warnings.length > 0) ? s.warnings.map((warning, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${isSaline ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                  {isSaline ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
                </div>
                <span className="text-xs text-slate-400">{warning}</span>
              </div>
            )) : (
              <div className="flex items-start gap-2">
                <div className="w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-emerald-500/20 text-emerald-400">
                  <CheckCircle2 className="w-3 h-3" />
                </div>
                <span className="text-xs text-slate-400">No warnings — water is within acceptable salinity range</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Probability Breakdown */}
      <div className="space-y-2">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Probability Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {[
            { label: 'Freshwater', value: safeNum(s.freshWaterProbability), color: 'bg-emerald-500' },
            { label: 'Brackish', value: safeNum(s.brackishProbability), color: 'bg-cyan-500' },
            { label: 'Saline', value: safeNum(s.salineProbability), color: 'bg-rb-brand' },
            { label: 'Hypersaline', value: safeNum(s.hypersalineProbability), color: 'bg-red-500' },
          ].map((item, i) => (
            <div key={i} className="p-3 rounded-lg bg-slate-800/30 border border-slate-800/50">
              <div className="text-[10px] text-slate-500 uppercase font-bold mb-1">{item.label}</div>
              <div className="text-lg font-bold text-white">{item.value.toFixed(1)}%</div>
              <div className="mt-1.5 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                <motion.div className={`h-full ${item.color} rounded-full`} initial={{ width: 0 }} animate={{ width: `${Math.min(100, item.value)}%` }} transition={{ duration: 0.5, delay: i * 0.1 }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Biological Impact & Compatible Processes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-800/50">
          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Biological Impact</h4>
          <p className="text-xs text-slate-300">{s.biologicalImpact || 'No biological impact data available.'}</p>
        </div>
        <div className="p-4 rounded-xl bg-slate-800/30 border border-slate-800/50">
          <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Recommended Treatment</h4>
          <p className="text-xs text-slate-300">{s.recommendedTreatment || 'No recommendation available.'}</p>
        </div>
      </div>
    </div>
  );
}
